import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Check, Loader2 } from "lucide-react";
import { fmtPrice } from "@/lib/stripe";
import { useTranslation } from "react-i18next";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("samtwo_platform_token") || localStorage.getItem("samtwo_token")}` } });
const PERK_KEYS = ["perk1", "perk2", "perk3", "perk4", "perk5"];

// Single Boss -> Don upgrade confirmation, shared by the post-checkout popup, the
// /pricing aware card and the Billing page. One copy set, one proration figure
// (GET /subscriptions/upgrade-preview) and one POST /subscriptions/upgrade, so the
// three surfaces can never drift apart. Callers own the success/error notification.
export const UpgradeToDonModal = ({ open, subscriptionId, onClose, onSuccess, onError, testid = "upgrade-to-don-modal" }) => {
  const { t } = useTranslation();
  const [preview, setPreview] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPreview(null); setPricing(null); setLoadError(false); setBusy(false);
    Promise.all([
      axios.get(`${API}/subscriptions/upgrade-preview`, authHeader()),
      axios.get(`${API}/settings/pricing`),
    ]).then(([{ data: pv }, { data: pr }]) => { setPreview(pv); setPricing(pr); })
      .catch(() => setLoadError(true));
  }, [open]);

  if (!open) return null;

  const confirm = async () => {
    if (!preview || busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/subscriptions/upgrade`, { subscription_id: subscriptionId, target_plan: preview.target_plan }, authHeader());
      onSuccess && onSuccess();
    } catch {
      setBusy(false);
      onError && onError();
      onClose && onClose();
    }
  };

  const don = (pricing && pricing.plans && preview && pricing.plans[preview.target_plan]) || {};

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[10010] flex items-center justify-center p-4" style={{ background: "rgba(10,15,26,0.7)" }} data-testid={testid}>
        <motion.div initial={{ scale: 0.95, y: 16 }} animate={{ scale: 1, y: 0 }}
          className="w-full max-w-sm rounded-2xl p-7 max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain" style={{ background: "var(--bg-surface)", border: "2px solid var(--accent-primary)" }}>
          <div className="flex items-center gap-2 mb-4">
            <Star size={20} fill="#f5b301" style={{ color: "#f5b301" }} />
            <h2 className="text-lg font-bold" style={{ color: "var(--text-primary)" }}>{t("pricing.upgradeToDon")}</h2>
          </div>

          {loadError ? (
            <p className="text-sm mb-5" style={{ color: "#ef4444" }} data-testid="upgrade-load-error">{t("pricing.noticeUpgradeLoadFail")}</p>
          ) : !preview ? (
            <div className="flex items-center justify-center py-6" data-testid="upgrade-loading"><Loader2 size={20} className="animate-spin" style={{ color: "var(--accent-primary)" }} /></div>
          ) : (
            <>
              <ul className="space-y-2 mb-5">
                {PERK_KEYS.map((k) => (
                  <li key={k} className="flex items-center gap-2 text-sm" style={{ color: "var(--text-primary)" }}>
                    <Check size={15} style={{ color: "var(--accent-primary)" }} /> {t(`commerce.${k}`)}
                  </li>
                ))}
              </ul>
              <p className="text-sm mb-5" style={{ color: "var(--text-primary)" }} data-testid="upgrade-amount">
                {t("pricing.upgradeDueToday", { amount: fmtPrice(preview.amount_due), price: fmtPrice(don.price), cycle: preview.cycle })}
              </p>
              {preview.early_bird && (
                <p className="text-sm mb-5 -mt-3 font-medium" style={{ color: "#b45309" }} data-testid="upgrade-eb-warning">{t("commerce.ebEndsUpgrade")}</p>
              )}
            </>
          )}

          <div className="flex flex-col gap-2">
            {!loadError && preview && (
              <button onClick={confirm} disabled={busy} data-testid="upgrade-confirm" className="w-full py-3 rounded-full text-sm font-semibold flex items-center justify-center gap-2" style={{ background: "var(--accent-primary)", color: "#fff", opacity: busy ? 0.7 : 1 }}>
                {busy ? <><Loader2 size={16} className="animate-spin" /> {t("pricing.upgrading")}</> : t("pricing.confirmUpgrade")}
              </button>
            )}
            <button onClick={onClose} data-testid="upgrade-cancel" className="w-full py-2.5 rounded-full text-sm font-medium" style={{ color: "var(--text-secondary)" }}>{t("pricing.notNow")}</button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default UpgradeToDonModal;
